import React, { useCallback, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import "../styles/jianpu.css";
import { songEvents, matchNote, parseJianpu, noteKey } from "../lib/jianpu";
import { getStarterSong } from "../lib/jianpuSongs";
import { getSavedSong } from "../lib/jianpuStorage";
import { playNote } from "../lib/homeKeyAudio";
import JianpuSheet from "../components/jianpu/JianpuSheet";
import EasyPlayGrid from "../components/jianpu/EasyPlayGrid";
import useJianpuPlayer, { MELODY_PATCH } from "../components/jianpu/useJianpuPlayer";
import useEasyPlay from "../components/jianpu/useEasyPlay";

const NOTE_NAMES = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
const midiName = (m) => `${NOTE_NAMES[m % 12]}${Math.floor(m / 12) - 1}`;

const MODES = [
  { id: "listen", label: "Listen" },
  { id: "along", label: "Play-along" },
  { id: "wait", label: "Wait for me" },
];

// Practice one song: listen with a moving highlight, play along against the
// clock for a score, or step through note by note at your own pace.
const JianpuPlay = () => {
  const { songId } = useParams();

  const loaded = useMemo(() => {
    const starter = getStarterSong(songId);
    if (starter) return starter.song;
    const saved = getSavedSong(songId);
    if (saved) return parseJianpu(saved.text).song;
    return null;
  }, [songId]);
  const song = loaded;

  const [mode, setMode] = useState("along");
  const [bpm, setBpm] = useState(song ? song.tempo : 90);
  const [metronome, setMetronome] = useState(true);
  const [guide, setGuide] = useState(true);

  // Scoring state. Refs hold the live-matching data (updated from MIDI
  // callbacks); state mirrors what the sheet needs to render.
  const [statuses, setStatuses] = useState({});
  const [summary, setSummary] = useState(null);
  const [hitKey, setHitKey] = useState(null);
  const matchedRef = useRef(new Set());
  const offsetsRef = useRef([]);
  const extrasRef = useRef(0);

  // Wait-for-me state: index into song.notes of the note to play next.
  const [targetIdx, setTargetIdx] = useState(-1);
  const [waitDone, setWaitDone] = useState(false);

  const player = useJianpuPlayer();

  const events = useMemo(
    () => (song ? songEvents(song, { bpm }) : []),
    [song, bpm]
  );
  const eventsRef = useRef(events);
  eventsRef.current = events;
  const noteEvents = useMemo(() => events.filter((e) => e.kind === "note"), [events]);

  const beatMs = 60000 / bpm;
  const windowMs = Math.min(320, beatMs * 0.6);

  const modeRef = useRef(mode);
  modeRef.current = mode;
  const targetRef = useRef(targetIdx);
  targetRef.current = targetIdx;

  const resetScoring = useCallback(() => {
    matchedRef.current = new Set();
    offsetsRef.current = [];
    extrasRef.current = 0;
    setStatuses({});
    setSummary(null);
    setHitKey(null);
  }, []);

  const nextNoteIndex = useCallback((fromNotesIdx) => {
    const notes = song ? song.notes : [];
    for (let i = fromNotesIdx; i < notes.length; i++) {
      if (notes[i].kind === "note") return i;
    }
    return -1;
  }, [song]);

  const advanceWait = useCallback(
    (fromIdx) => {
      const next = nextNoteIndex(fromIdx + 1);
      setTargetIdx(next);
      if (next === -1) setWaitDone(true);
    },
    [nextNoteIndex]
  );

  const handleWaitInput = useCallback(
    (played) => {
      const idx = targetRef.current;
      if (idx < 0 || !song) return;
      const want = song.notes[idx];
      const ok =
        want.degree === played.degree &&
        want.octave === played.octave &&
        want.accidental === played.accidental;
      if (ok) {
        const ev = eventsRef.current[idx];
        if (ev && ev.midi != null) playNote(MELODY_PATCH, ev.midi, 350);
        setStatuses((prev) => ({ ...prev, [idx]: "hit" }));
        setHitKey(noteKey(played));
        advanceWait(idx);
      }
    },
    [song, advanceWait]
  );

  const handleNote = useCallback(
    (played) => {
      if (modeRef.current === "wait") {
        handleWaitInput(played);
        return;
      }
      if (modeRef.current !== "along" || !player.playing) return;
      const t = player.getSongTimeMs();
      if (t < -windowMs) return; // still in the count-in
      const idx = matchNote(eventsRef.current, matchedRef.current, played, t, windowMs);
      if (idx === -1) {
        extrasRef.current += 1;
        return;
      }
      matchedRef.current.add(idx);
      offsetsRef.current.push(t - eventsRef.current[idx].startMs);
      setStatuses((prev) => ({ ...prev, [idx]: "hit" }));
      setHitKey(noteKey(played));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleWaitInput, windowMs, player.playing]
  );

  const ep = useEasyPlay({ onNote: handleNote });

  const finishAlong = useCallback(() => {
    const matched = matchedRef.current;
    const final = {};
    for (const ev of eventsRef.current) {
      if (ev.kind === "note") final[ev.index] = matched.has(ev.index) ? "hit" : "miss";
    }
    setStatuses(final);
    const total = eventsRef.current.filter((e) => e.kind === "note").length;
    const hits = matched.size;
    const offsets = offsetsRef.current;
    setSummary({
      total,
      hits,
      misses: total - hits,
      extras: extrasRef.current,
      accuracy: total ? hits / total : 0,
      avgOffsetMs: offsets.length
        ? Math.round(offsets.reduce((a, b) => a + b, 0) / offsets.length)
        : 0,
    });
  }, []);

  const markPassedMisses = useCallback(
    (ev) => {
      if (modeRef.current !== "along") return;
      const cutoff = ev.startMs - windowMs;
      setStatuses((prev) => {
        let changed = false;
        const next = { ...prev };
        for (const e of eventsRef.current) {
          if (e.kind !== "note" || e.startMs >= cutoff) continue;
          if (!matchedRef.current.has(e.index) && next[e.index] !== "miss") {
            next[e.index] = "miss";
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    },
    [windowMs]
  );

  if (!song) {
    return (
      <div className="jp-root">
        <div className="jp-shell">
          <Link to="/jianpu" className="jp-back">
            ← jianpu studio
          </Link>
          <div className="jp-panel" style={{ marginTop: "2rem", maxWidth: "28rem" }}>
            <p>Hmm, no song lives at this address. It may have been a saved song on another device.</p>
          </div>
        </div>
      </div>
    );
  }

  const startRun = () => {
    resetScoring();
    setTargetIdx(-1);
    setWaitDone(false);
    player.start({
      events,
      bpm,
      beatsPerBar: song.beatsPerBar,
      totalBeats: song.totalBeats,
      metronome,
      guide: mode === "listen" ? true : guide,
      countInBeats: mode === "along" ? song.beatsPerBar : 0,
      onEvent: markPassedMisses,
      onDone: mode === "along" ? finishAlong : undefined,
    });
  };

  const startWait = () => {
    resetScoring();
    setWaitDone(false);
    setTargetIdx(nextNoteIndex(0));
  };

  const stopAll = () => {
    player.stop();
    setTargetIdx(-1);
    setWaitDone(false);
  };

  const running = player.playing || targetIdx >= 0;

  // What the grid should point at: the wait-mode target, or during a
  // play-along the first not-yet-matched note at/after the playhead.
  let gridTarget = null;
  if (mode === "wait" && targetIdx >= 0) {
    gridTarget = song.notes[targetIdx];
  } else if (mode === "along" && player.playing) {
    const upcoming = noteEvents.find(
      (e) => e.index >= player.activeIndex && !statuses[e.index]
    );
    if (upcoming) gridTarget = upcoming;
  }

  const doneCount = Object.values(statuses).filter((s) => s === "hit").length;

  return (
    <div className="jp-root">
      <div className="jp-shell">
        <div className="jp-btn-row jp-no-print" style={{ justifyContent: "space-between" }}>
          <Link to="/jianpu" className="jp-back">
            ← jianpu studio
          </Link>
          <span className="jp-kicker">practice room</span>
        </div>

        {!ep.supported && (
          <div className="jp-banner jp-banner--bad jp-no-print" style={{ marginTop: "1rem" }}>
            This browser doesn't speak Web MIDI, so play-along scoring can't
            hear the instrument — Chrome or Edge can. Listening mode still
            works here, and the on-screen buttons work in wait-for-me mode.
          </div>
        )}
        {ep.supported && !ep.connected && (
          <div className="jp-banner jp-no-print" style={{ marginTop: "1rem" }}>
            No MIDI instrument yet — plug the EasyPlay in over USB and it'll
            appear here. On-screen buttons work in the meantime.
          </div>
        )}

        <div className="jp-play-grid">
          <div>
            <JianpuSheet
              song={song}
              activeIndex={player.activeIndex}
              targetIndex={mode === "wait" ? targetIdx : -1}
              statuses={statuses}
            />
          </div>

          <aside className="jp-side jp-no-print">
            <div className="jp-panel">
              <div className="jp-seg" role="tablist" aria-label="practice mode">
                {MODES.map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    className={mode === m.id ? "jp-seg--on" : ""}
                    onClick={() => {
                      stopAll();
                      resetScoring();
                      setMode(m.id);
                    }}
                  >
                    {m.label}
                  </button>
                ))}
              </div>

              <div style={{ marginTop: "0.9rem" }}>
                <div className="jp-label">tempo · ♩ = {bpm}</div>
                <input
                  type="range"
                  min="50"
                  max="180"
                  value={bpm}
                  disabled={running}
                  onChange={(e) => setBpm(Number(e.target.value))}
                  style={{ width: "100%" }}
                />
              </div>

              {mode !== "wait" && (
                <div className="jp-btn-row" style={{ marginTop: "0.6rem" }}>
                  <button
                    type="button"
                    className={`jp-btn${metronome ? " jp-btn--active" : ""}`}
                    onClick={() => setMetronome((v) => !v)}
                  >
                    metronome {metronome ? "on" : "off"}
                  </button>
                  {mode === "along" && (
                    <button
                      type="button"
                      className={`jp-btn${guide ? " jp-btn--active" : ""}`}
                      onClick={() => setGuide((v) => !v)}
                    >
                      guide melody {guide ? "on" : "off"}
                    </button>
                  )}
                </div>
              )}

              <div className="jp-btn-row" style={{ marginTop: "0.9rem" }}>
                {!running ? (
                  <button
                    type="button"
                    className="jp-btn jp-btn--primary"
                    onClick={mode === "wait" ? startWait : startRun}
                  >
                    ▶ {mode === "along" ? "start (1 bar count-in)" : "start"}
                  </button>
                ) : (
                  <button type="button" className="jp-btn" onClick={stopAll}>
                    ■ stop
                  </button>
                )}
              </div>

              {mode === "wait" && targetIdx >= 0 && (
                <p style={{ marginTop: "0.7rem", fontSize: "0.85rem", color: "var(--jp-ink-dim)" }}>
                  {doneCount} / {noteEvents.length} notes — it waits for the
                  right one, take your time.
                </p>
              )}
              {waitDone && (
                <p style={{ marginTop: "0.7rem", fontSize: "0.9rem", color: "var(--jp-green)" }}>
                  ✿ whole song, note by note. Play it again a touch faster?
                </p>
              )}
            </div>

            <div className="jp-panel">
              <div className="jp-label">instrument</div>
              <p style={{ fontSize: "0.85rem", marginTop: "0.4rem" }}>
                {ep.connected
                  ? `connected: ${ep.deviceNames.join(", ")}`
                  : "nothing connected"}
              </p>
              <p style={{ fontSize: "0.85rem", color: "var(--jp-ink-dim)", marginTop: "0.3rem" }}>
                ① = {midiName(ep.tonicMidi)} (MIDI {ep.tonicMidi})
              </p>
              {ep.calibrating ? (
                <div style={{ marginTop: "0.5rem" }}>
                  <p style={{ fontSize: "0.85rem", color: "var(--jp-red)" }}>
                    …press your ① button (bottom-left big one)
                  </p>
                  <button
                    type="button"
                    className="jp-btn"
                    style={{ marginTop: "0.4rem" }}
                    onClick={ep.cancelCalibration}
                  >
                    cancel
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className="jp-btn"
                  style={{ marginTop: "0.5rem" }}
                  onClick={ep.startCalibration}
                  disabled={!ep.connected}
                >
                  calibrate — press ①
                </button>
              )}
              <p style={{ fontSize: "0.78rem", color: "var(--jp-ink-dim)", marginTop: "0.5rem" }}>
                Moved the transpose dial? Recalibrate. Scoring compares scale
                degrees, so any dial position works.
              </p>
            </div>

            <div className="jp-panel">
              <div className="jp-label" style={{ marginBottom: "0.6rem" }}>
                the buttons
              </div>
              <EasyPlayGrid
                target={gridTarget}
                pressed={ep.pressed}
                hitKey={hitKey}
                onPress={
                  mode === "wait" && targetIdx >= 0
                    ? (cell) => handleWaitInput({ accidental: 0, ...cell })
                    : undefined
                }
              />
            </div>

            {summary && (
              <div className="jp-panel">
                <div className="jp-label" style={{ marginBottom: "0.6rem" }}>
                  the verdict
                </div>
                <div className="jp-score">
                  <div className="jp-score-big">
                    <b>{Math.round(summary.accuracy * 100)}%</b>
                  </div>
                  <span>
                    hits <b>{summary.hits}</b>/{summary.total}
                  </span>
                  <span>
                    stray notes <b>{summary.extras}</b>
                  </span>
                  <span style={{ gridColumn: "1 / -1", fontSize: "0.82rem", color: "var(--jp-ink-dim)" }}>
                    {summary.hits === 0
                      ? "the song played; the buttons await"
                      : summary.avgOffsetMs > 40
                        ? `running ~${summary.avgOffsetMs}ms behind the beat — dragging a little`
                        : summary.avgOffsetMs < -40
                          ? `running ~${-summary.avgOffsetMs}ms ahead — eager!`
                          : "timing right in the pocket"}
                  </span>
                </div>
              </div>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
};

export default JianpuPlay;
